import { createRouter, createWebHistory } from 'vue-router';
import { setLocale, getLocale } from '@/utils/i18nHelpers';
import store from '@/store';
import userRoles from '@/utils/enums/userRoles';
import { authGuard } from '@/auth';
import i18n from '@/i18n';

const Leads = () => import('./app/leads/views/LeadsPage.vue');
const LeadDetails = () => import('./app/leads/views/_LeadDetailsPage.vue'); // When clicking on an individual lead from the Leads Export page
const LeadExport = () => import('./app/leads/views/_Leads.vue'); // Leads Export
const Registration = () => import('./app/authentication/views/RegistrationPage.vue');
const ResetPassword = () => import('./app/authentication/views/ResetPassword.vue');
const Dashboard = () => import('./app/dashboard/views/DashboardPageV3.vue');
const AddUser = () => import('./app/users/views/_AddUserPage.vue');
const EditUser = () => import('./app/users/views/_EditUserPage.vue');
const ListUser = () => import('./app/users/views/_ListUserPage.vue');
const Users = () => import('./app/users/views/UsersPage.vue');
const ReviewRequests = () => import('./app/reviews/views/ReviewRequests.vue');
const Profile = () => import('./app/profile/views/MyProfilePage.vue');
const Error = () => import('./app/global/views/ErrorPage.vue');
const ProductSettingsPage = () => import('./app/productSettings/views/ProductSettingsPage.vue');
const ProductSettings = () => import('./app/productSettings/views/_ProductSettings.vue');
const AutoVerifyResearchSuite = () => import('./app/productSettings/views/_AutoVerifyResearchSuite.vue');
const LeadEmailSettings = () => import('./app/productSettings/views/_LeadEmailSettings.vue');
const Support = () => import('./app/support/views/Support.vue');
const AuthError = () => import('./app/global/views/AuthErrorPage.vue');
const BusinessError = () => import('./app/global/views/BusinessErrorPage.vue');
const LinkExpired = () => import('./app/global/views/LinkExpiredPage.vue');
const UnblockAccount = () => import('./app/global/views/UnblockAccountPage.vue');
const CreditCardCapturePage = () => import('./app/creditCardCapture/views/CreditCardCapturePage.vue');
const StyleGuide = () => import('./app/style-guide/views/StyleGuide.vue');

// Layouts
const routes = [
  {
    path: '',
    redirect: { name: 'dashboard' },
    meta: {
      layout: 'NonAuthenticated',
    },
  },
  {
    path: '/register',
    name: 'register',
    component: Registration,
    meta: {
      layout: 'NonAuthenticated',
    },
  },
  {
    path: '/reset-password',
    name: 'resetPassword',
    component: ResetPassword,
    meta: {
      layout: 'NonAuthenticated',
    },
    props: true,
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: Dashboard,
    meta: {
      titleKey: 'pageTitles.dashboard',
      menuName: 'dashboard',
      icon: 'chart-pie',
      authorize: [userRoles.ADMIN, userRoles.FINANCE, userRoles.AV_ADMIN, userRoles.SUPPORT, userRoles.SALES],
      layout: 'Authenticated',
    },
  },
  {
    path: '/leads',
    component: Leads,
    meta: {
      titleKey: 'pageTitles.leads',
      menuName: 'leads',
      icon: 'clipboard-list',
      authorize: [userRoles.ADMIN, userRoles.FINANCE, userRoles.AV_ADMIN, userRoles.SUPPORT, userRoles.SALES],
      layout: 'Authenticated',
    },
    children: [
      {
        path: '',
        name: 'leadList',
        component: LeadExport,
        meta: {
          titleKey: 'pageTitles.listLeads',
          authorize: [userRoles.ADMIN, userRoles.FINANCE, userRoles.AV_ADMIN, userRoles.SUPPORT, userRoles.SALES],
          layout: 'Authenticated',
        },
      },
      {
        path: ':leadId',
        name: 'leadDetails',
        component: LeadDetails,
        props: true,
        meta: {
          titleKey: 'pageTitles.leadDetails',
          authorize: [userRoles.ADMIN, userRoles.FINANCE, userRoles.AV_ADMIN, userRoles.SUPPORT, userRoles.SALES],
          layout: 'Authenticated',
        },
      },
    ],
  },
  {
    path: '/users',
    component: Users,
    meta: {
      titleKey: 'pageTitles.users',
      menuName: 'users',
      icon: 'user-friends',
      authorize: [userRoles.ADMIN, userRoles.AV_ADMIN, userRoles.SUPPORT, userRoles.SALES],
      layout: 'Authenticated',
    },
    children: [
      {
        path: '',
        name: 'userList',
        component: ListUser,
        meta: {
          titleKey: 'pageTitles.listUser',
          authorize: [userRoles.ADMIN, userRoles.AV_ADMIN, userRoles.SUPPORT, userRoles.SALES],
          layout: 'Authenticated',
        },
      },
      {
        path: 'add',
        name: 'addUser',
        component: AddUser,
        meta: {
          titleKey: 'pageTitles.addUser',
          authorize: [userRoles.ADMIN, userRoles.AV_ADMIN, userRoles.SUPPORT, userRoles.SALES],
          layout: 'Authenticated',
        },
      },
      {
        path: 'edit/:id',
        props: true,
        name: 'editUser',
        component: EditUser,
        beforeEnter: (to, from, next) => {
          if (from.name === 'userList') {
            next();
          } else {
            router.push('/users', () => {});
          }
        },
        meta: {
          titleKey: 'pageTitles.editUser',
          authorize: [userRoles.ADMIN, userRoles.AV_ADMIN, userRoles.SUPPORT, userRoles.SALES],
          layout: 'Authenticated',
        },
      },
    ],
  },
  {
    path: '/review-requests',
    component: ReviewRequests,
    meta: {
      titleKey: 'pageTitles.reviewRequests',
      menuName: 'reviews',
      icon: 'star',
      authorize: [userRoles.ADMIN, userRoles.AV_ADMIN, userRoles.SUPPORT, userRoles.SALES],
      product: 'reviews',
      layout: 'Authenticated',
    },
  },
  {
    path: '/product-settings',
    component: ProductSettingsPage,
    meta: {
      titleKey: 'pageTitles.productSettings',
      menuName: 'productSettings',
      icon: 'tag',
      authorize: [userRoles.ADMIN, userRoles.AV_ADMIN, userRoles.FINANCE],
      layout: 'Authenticated',
    },
    children: [
      {
        path: '',
        name: 'productSettings',
        component: ProductSettings,
        meta: {
          titleKey: 'pageTitles.productSettings',
          authorize: [userRoles.ADMIN, userRoles.AV_ADMIN, userRoles.FINANCE],
          layout: 'Authenticated',
        },
      },
      {
        path: 'lead-email-settings',
        name: 'leadEmailSettings',
        component: LeadEmailSettings,
        meta: {
          titleKey: 'pageTitles.leadEmailSettings',
          authorize: [userRoles.ADMIN, userRoles.AV_ADMIN],
          layout: 'Authenticated',
        },
      },
      {
        path: 'autoverify-research-suite',
        name: 'autoVerifyResearchSuite',
        component: AutoVerifyResearchSuite,
        meta: {
          titleKey: 'pageTitles.autoVerifyResearchSuite',
          authorize: [userRoles.ADMIN, userRoles.AV_ADMIN, userRoles.FINANCE],
          layout: 'Authenticated',
        },
      },
    ],
  },
  {
    path: '/profile',
    name: 'profile',
    component: Profile,
    meta: {
      titleKey: 'pageTitles.profile',
      menuName: 'profile',
      icon: 'user-circle',
      authorize: [userRoles.ADMIN, userRoles.FINANCE, userRoles.AV_ADMIN, userRoles.SUPPORT, userRoles.SALES],
      layout: 'Authenticated',
    },
  },
  {
    path: '/support',
    name: 'support',
    component: Support,
    meta: {
      titleKey: 'pageTitles.support',
      menuName: 'helpAndSupport',
      icon: 'headset',
      authorize: [userRoles.ADMIN, userRoles.FINANCE, userRoles.AV_ADMIN, userRoles.SUPPORT, userRoles.SALES],
      layout: 'Authenticated',
    },
  },
  {
    path: '/credit-card-entry/:masterBusinessId',
    name: 'creditCardCapturePage',
    component: CreditCardCapturePage,
  },
  {
    path: '/error',
    name: 'error',
    component: Error,
    meta: {
      layout: 'NonAuthenticated',
    },
  },
  {
    path: '/auth-error',
    name: 'authError',
    component: AuthError,
    meta: {
      layout: 'NonAuthenticated',
    },
  },
  {
    path: '/business-error',
    name: 'businessError',
    component: BusinessError,
    meta: {
      layout: 'NonAuthenticated',
    },
  },
  {
    path: '/link-expired',
    name: 'linkExpired',
    component: LinkExpired,
    meta: {
      layout: 'NonAuthenticated',
    },
  },
  {
    path: '/unblock-account',
    name: 'unblockAccount',
    component: UnblockAccount,
    meta: {
      layout: 'NonAuthenticated',
    },
  },
  {
    path: '/:catchAll(.*)',
    redirect: { name: 'error' },
  },
  {
    path: '/login',
    redirect: { name: 'dashboard' },
    meta: {
      layout: 'NonAuthenticated',
    },
  },
  {
    path: '/style-guide',
    name: 'styleGuide',
    component: StyleGuide,
    meta: {
      authorize: [userRoles.AV_ADMIN],
      layout: 'Authenticated',
    },
  },
];

const router = createRouter({
  history: createWebHistory('/'),
  routes,
  scrollBehavior() {
    return { left: 0, top: 0 };
  },
});

router.beforeEach(async (to, from) => {
  await store.dispatch('resetErrorMessage');
  if (to.query.language) {
    setLocale(to.query.language);
  }
  if (to.meta.authorize) {
    if (to.query.error) {
      // Redirect to error page
      return { name: 'authError', query: { error: to.query.error, error_description: to.query.error_description } };
    } else {
      // Await the result of your AuthGuard
      return await authGuard(to, from, getLocale());
    }
  }
  // Allow navigation
  return true;
});

router.afterEach(to => {
  // Set HTML document title
  const pageTitle = to.meta.titleKey ? i18n.global.t(to.meta.titleKey) : i18n.global.t('pageTitle');
  if (pageTitle) {
    document.title = pageTitle;
  }

  // Update Intercom on route change
  if (window.Intercom) {
    window.Intercom('update', {
      page_title: pageTitle,
    });
  }
});

export default router;
